import {
  CHANNEL_SHARE_NOTIFICATION_RECEIVE,
  CHANNEL_SHARE_NOTIFICATION_ACCEPTED,
  CLIENT_LIBRARY_NOTIFICATION_RECEIVE,
  LEAK_TRACING_NOTIFICATION_RECEIVE,
  NOTIFICATION_PANE_DISMISS,
  NOTIFICATION_PANE_EXPAND,
  NOTIFICATION_PANE_TOGGLE,
  PLAYLIST_NOTIFICATION_RECEIVE,
  PLAYLIST_NOTIFICATION_RESET,
  PLAYLIST_NOTIFICATIONS_BATCH_LOAD,
  BRIEF_SUBMISSION_NOTIFICATION_RECEIVE,
  MENTION_NOTIFICATION_RECEIVE,
  REACTION_NOTIFICATION_RECEIVE,
} from '@disco/constants/ActionTypes'
import lodashSort from 'lodash/sortBy'
import lodashUniqBy from 'lodash/uniqBy'
const initialState = {
  unreadNotifications: [] as Array<any>,
  isNotificationPaneVisible: false,
  isNotificationPaneExpanded: false,
}
export type NotificationState = typeof initialState

const sortNotifications = list => lodashSort(list, 'timestamp').reverse()

const getUnreadNotifications = (newNotification, existingNotifications) => {
  const updatedNotifications = lodashUniqBy(
    [newNotification, ...existingNotifications],
    // Live mention payloads may not carry `id`; fall back to the unique keys the
    // notification pane also keys on so distinct mentions don't collapse into one.
    item => item.id ?? item.api_url ?? item.message_id,
  )
  return sortNotifications(updatedNotifications)
}

export function notificationsReducer(
  state: NotificationState = initialState,
  action: Record<string, any> = {},
): NotificationState {
  const { type } = action

  switch (type) {
    case PLAYLIST_NOTIFICATIONS_BATCH_LOAD: {
      const batch = action.notifications
      return {
        ...state,
        // reset state here, because sortNotifications doesn't merge them by ID
        unreadNotifications: sortNotifications(batch),
      }
    }

    case PLAYLIST_NOTIFICATION_RECEIVE: {
      const { notification } = action

      if (
        notification.type === 'aggregated' &&
        typeof notification.playlist_id === 'number'
      ) {
        const notificationToReplace = state.unreadNotifications.find(
          n => n.playlist_id === notification.playlist_id,
        )

        if (notificationToReplace) {
          // replace notification by playlist_id
          return {
            ...state,
            unreadNotifications: sortNotifications(
              state.unreadNotifications.map(unreadNotification => {
                return notificationToReplace === unreadNotification
                  ? notification
                  : unreadNotification
              }),
            ),
          }
        }
      }

      return {
        ...state,
        unreadNotifications: getUnreadNotifications(
          notification,
          state.unreadNotifications,
        ),
      }
    }

    case PLAYLIST_NOTIFICATION_RESET:
      return { ...state, unreadNotifications: [] }

    case NOTIFICATION_PANE_TOGGLE:
      return {
        ...state,
        isNotificationPaneVisible: action.isNotificationPaneVisible,
        isNotificationPaneExpanded: false,
      }

    case NOTIFICATION_PANE_EXPAND:
      return {
        ...state,
        isNotificationPaneVisible: true,
        isNotificationPaneExpanded: true,
      }

    case NOTIFICATION_PANE_DISMISS:
      return {
        ...state,
        unreadNotifications: state.unreadNotifications.filter(notification => {
          return notification.api_url !== action.notification.api_url
        }),
      }

    case BRIEF_SUBMISSION_NOTIFICATION_RECEIVE:
    case MENTION_NOTIFICATION_RECEIVE:
    case REACTION_NOTIFICATION_RECEIVE:
    case CHANNEL_SHARE_NOTIFICATION_RECEIVE:
    case CHANNEL_SHARE_NOTIFICATION_ACCEPTED:
    case CLIENT_LIBRARY_NOTIFICATION_RECEIVE:
    case LEAK_TRACING_NOTIFICATION_RECEIVE: {
      const { notification } = action
      return {
        ...state,
        unreadNotifications: getUnreadNotifications(
          notification,
          state.unreadNotifications,
        ),
      }
    }

    default:
      return state
  }
}
